'use client';

import React, { useEffect, useState, useMemo } from 'react';
import BaseMechanicShell from './BaseMechanicShell';
import { Microscope } from 'lucide-react';

interface DiagnosticLabMechanicProps {
  lessonSlug: string;
  mechanicId?: string;
  id?: string;
  prompt?: string;
  tier?: string;
  localHints?: { id: string; hint: string }[];
  localValidator?: { kind?: string; correctAnswer?: string; hints?: any[] };
  payload: {
    scenario?: string;
    symptoms?: string[];
    diagnoses?: any[];
    parameters?: any[];
    _hasHints?: boolean;
  };
}

export default function DiagnosticLabMechanic({ lessonSlug, mechanicId, id, prompt, tier, localHints, localValidator, payload }: DiagnosticLabMechanicProps) {
  const actualMechanicId = mechanicId || id || '';

  return (
    <BaseMechanicShell
      lessonSlug={lessonSlug}
      mechanicId={actualMechanicId}
      prompt={payload?.scenario || prompt || ''}
      icon={<Microscope className="w-5 h-5" />}
      iconLabel="Diagnostic Lab — Diagnose the problem and adjust treatment parameters"
      payload={payload}
      tier={tier}
      localHints={localHints}
      localValidator={localValidator}
    >
      {(submissionState, setAnswer) => (
        <DiagnosticLabMechanicInner payload={payload} submissionState={submissionState} setAnswer={setAnswer} />
      )}
    </BaseMechanicShell>
  );
}

function DiagnosticLabMechanicInner({ payload, submissionState, setAnswer }: any) {
  const symptoms = payload?.symptoms || [];
  const diagnoses = payload?.diagnoses || [];
  const parameters = useMemo(() => payload?.parameters || [], [payload?.parameters]);

  const [diagnosisId, setDiagnosisId] = useState<string | null>(null);
  const [paramValues, setParamValues] = useState<Record<string, number>>({});

  useEffect(() => {
    if (parameters.length > 0 && Object.keys(paramValues).length === 0) {
      const initial: Record<string, number> = {};
      parameters.forEach((p: any) => {
        initial[p.id] = p.min || 0;
      });
      setParamValues(initial);
    }
  }, [parameters, paramValues]);

  useEffect(() => {
    if (diagnosisId && Object.keys(paramValues).length > 0) {
      setAnswer({ diagnosisId, parameters: paramValues });
    } else {
      setAnswer(null);
    }
  }, [diagnosisId, paramValues, setAnswer]);

  return (
    <div className="flex flex-col gap-5">
      {/* PHASE 1: INDICATORS + ROOT CAUSE */}
      <div className="bg-card border border-border p-5 rounded-2xl shadow-sm">
        <h3 className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-4">Phase 1 — Diagnose</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="flex flex-col gap-2">
            <h4 className="text-xs uppercase font-semibold text-muted-foreground">Indicators</h4>
            <ul className="space-y-1.5">
              {symptoms.map((symptom: string, idx: number) => (
                <li key={idx} className="text-sm bg-destructive/10 p-2.5 rounded-lg border border-destructive/20 flex items-start gap-2">
                  <span className="mt-0.5 shrink-0 text-destructive/60">●</span>
                  <span>{symptom}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex flex-col gap-2">
            <h4 className="text-xs uppercase font-semibold text-muted-foreground">Root cause</h4>
            {diagnoses.map((diag: any) => (
              <button
                type="button"
                key={diag.id}
                disabled={submissionState !== 'idle' && submissionState !== 'error'}
                onClick={() => setDiagnosisId(diag.id)}
                className={`text-left p-3 rounded-lg border-2 transition-all text-sm ${
                  diagnosisId === diag.id 
                    ? 'border-primary bg-primary/10 font-medium text-primary shadow-[0_0_12px_rgba(var(--primary),0.15)]' 
                    : 'border-border bg-background hover:border-primary/50 text-foreground/80'
                }`}
              >
                {diag.content}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* PHASE 2: PARAMETERS */}
      <div className={`transition-all duration-500 ${diagnosisId ? 'opacity-100' : 'opacity-40 pointer-events-none blur-[0.5px]'}`}>
        <div className="bg-card border border-border p-5 rounded-2xl shadow-sm">
          <h3 className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-4">Phase 2 — Adjust</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {parameters.map((p: any) => {
              const val = paramValues[p.id] || 0;
              const pct = ((val - (p.min || 0)) / ((p.max || 100) - (p.min || 0))) * 100;
              return (
                <div key={p.id} className="bg-muted/30 p-4 rounded-xl border border-border/50 flex flex-col gap-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{p.label}</span>
                    <span className="text-base font-mono font-bold text-primary">{val}</span>
                  </div>
                  <div className="relative h-7 flex items-center">
                    <div className="absolute inset-0 h-2 my-auto bg-muted rounded-full border border-border/30 overflow-hidden">
                      <div className="h-full bg-primary/30 rounded-full transition-all duration-100" style={{ width: `${pct}%` }} />
                    </div>
                    <input 
                      type="range"
                      min={p.min || 0}
                      max={p.max || 100}
                      step={p.step || 25}
                      disabled={submissionState !== 'idle' && submissionState !== 'error'}
                      value={val}
                      onChange={(e) => setParamValues(prev => ({...prev, [p.id]: parseFloat(e.target.value)}))}
                      className="w-full relative z-10 opacity-0 cursor-pointer h-full"
                    />
                    <div 
                      className="absolute h-5 w-5 bg-primary rounded-full border-[3px] border-background shadow-md pointer-events-none -ml-2.5 transition-all duration-100"
                      style={{ left: `${pct}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-[10px] text-muted-foreground/60 font-mono px-0.5">
                    <span>0</span>
                    <span>25</span>
                    <span>50</span>
                    <span>75</span>
                    <span>100</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
